import React, { useState } from "react";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { leadStatuses } from "../../data/procrmData";

function AddLead({ setLeads, setActiveView }) {
  const [saving, setSaving] = useState(false);
  const [searchingAddress, setSearchingAddress] = useState(false);
  const [addressResults, setAddressResults] = useState([]);

  const [leadForm, setLeadForm] = useState({
    business: "",
    industry: "",
    location: "",
    latitude: "",
    longitude: "",
    contact: "",
    website: "",
    social: "",
    platform: "Google Maps",
    problem: "",
    service: "Starter Website",
    status: "Not Contacted",
    priority: "Medium",
    followUpDate: "",
    notes: "",
  });

  const handleLeadChange = (event) => {
    const { name, value } = event.target;

    setLeadForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "location") {
      setAddressResults([]);
      setLeadForm((prev) => ({
        ...prev,
        location: value,
        latitude: "",
        longitude: "",
      }));
    }
  };

  const resetForm = () => {
    setLeadForm({
      business: "",
      industry: "",
      location: "",
      latitude: "",
      longitude: "",
      contact: "",
      website: "",
      social: "",
      platform: "Google Maps",
      problem: "",
      service: "Starter Website",
      status: "Not Contacted",
      priority: "Medium",
      followUpDate: "",
      notes: "",
    });

    setAddressResults([]);
  };

  const searchAddress = async () => {
    if (!leadForm.location.trim()) {
      alert("Please type a business name, area, suburb, city, or address.");
      return;
    }

    try {
      setSearchingAddress(true);

      const query = encodeURIComponent(`${leadForm.location}, South Africa`);

      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${query}&addressdetails=1&limit=6`
      );

      const data = await response.json();
      setAddressResults(data || []);

      if (!data || data.length === 0) {
        alert("No address found. Try a more specific suburb, city, or street.");
      }
    } catch (error) {
      console.error("Address search error:", error);
      alert("Could not search address right now.");
    } finally {
      setSearchingAddress(false);
    }
  };

  const selectAddressResult = (place) => {
    const displayName = place.display_name || "";
    const lat = place.lat ? Number(place.lat) : null;
    const lng = place.lon ? Number(place.lon) : null;

    setLeadForm((prev) => ({
      ...prev,
      location: displayName,
      latitude: lat,
      longitude: lng,
    }));

    setAddressResults([]);
  };

  const addLead = async (event) => {
    event.preventDefault();

    if (!leadForm.business.trim()) {
      alert("Please add the business name.");
      return;
    }

    try {
      setSaving(true);

      const cleanedLead = {
        business: leadForm.business.trim(),
        industry: leadForm.industry.trim(),
        location: leadForm.location.trim(),
        latitude: leadForm.latitude ? Number(leadForm.latitude) : null,
        longitude: leadForm.longitude ? Number(leadForm.longitude) : null,
        contact: leadForm.contact.trim(),
        website: leadForm.website.trim(),
        social: leadForm.social.trim(),
        platform: leadForm.platform,
        problem: leadForm.problem.trim(),
        service: leadForm.service.trim(),
        status: leadForm.status,
        priority: leadForm.priority,
        followUpDate: leadForm.followUpDate,
        notes: leadForm.notes.trim(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const docRef = await addDoc(collection(db, "leads"), cleanedLead);

      const newLeadForScreen = {
        id: docRef.id,
        ...cleanedLead,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      setLeads((prev) => [newLeadForScreen, ...prev]);

      resetForm();
      alert("Lead saved successfully.");
      setActiveView("leads");
    } catch (error) {
      console.error("Error saving lead:", error);
      alert("Could not save lead. Please check your Firebase setup.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="procrm-panel crm-section">
      <div className="panel-heading">
        <div>
          <span className="eyebrow">Lead Capture</span>
          <h2>Add a new business lead</h2>
        </div>
      </div>

      <form className="lead-form" onSubmit={addLead}>
        <div className="form-row">
          <label>
            Business Name
            <input
              name="business"
              value={leadForm.business}
              onChange={handleLeadChange}
              required
            />
          </label>

          <label>
            Industry
            <input
              name="industry"
              value={leadForm.industry}
              onChange={handleLeadChange}
            />
          </label>
        </div>

        <div className="form-row">
          <label className="location-lookup-field">
            Location / Address Details
            <div className="location-search-row">
              <input
                name="location"
                value={leadForm.location}
                onChange={handleLeadChange}
                placeholder="Type address, suburb, city, or business location"
              />

              <button
                type="button"
                className="procrm-btn-secondary"
                onClick={searchAddress}
                disabled={searchingAddress}
              >
                {searchingAddress ? "Finding..." : "Find"}
              </button>
            </div>

            {addressResults.length > 0 && (
              <div className="address-results">
                {addressResults.map((place) => (
                  <button
                    type="button"
                    key={`${place.place_id}-${place.lat}-${place.lon}`}
                    onClick={() => selectAddressResult(place)}
                  >
                    {place.display_name}
                  </button>
                ))}
              </div>
            )}
          </label>

          <label>
            Contact / Email / WhatsApp
            <input
              name="contact"
              value={leadForm.contact}
              onChange={handleLeadChange}
            />
          </label>
        </div>

        <div className="form-row">
          <label>
            Website
            <input
              name="website"
              value={leadForm.website}
              onChange={handleLeadChange}
              placeholder="URL or None found"
            />
          </label>

          <label>
            Instagram / Facebook
            <input
              name="social"
              value={leadForm.social}
              onChange={handleLeadChange}
            />
          </label>
        </div>

        <div className="form-row">
          <label>
            Platform Used
            <select
              name="platform"
              value={leadForm.platform}
              onChange={handleLeadChange}
            >
              <option>Google Maps</option>
              <option>Instagram</option>
              <option>Facebook</option>
              <option>LinkedIn</option>
              <option>WhatsApp</option>
              <option>Walk-in</option>
              <option>Referral</option>
              <option>Other</option>
            </select>
          </label>

          <label>
            Status
            <select
              name="status"
              value={leadForm.status}
              onChange={handleLeadChange}
            >
              {leadStatuses.map((status) => (
                <option key={status}>{status}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="form-row">
          <label>
            Problem Found
            <input
              name="problem"
              value={leadForm.problem}
              onChange={handleLeadChange}
              placeholder="No website, outdated website, no catalogue..."
            />
          </label>

          <label>
            Recommended Solution
            <input
              name="service"
              value={leadForm.service}
              onChange={handleLeadChange}
              placeholder="Starter website, quote form, catalogue..."
            />
          </label>
        </div>

        <div className="form-row">
          <label>
            Priority
            <select
              name="priority"
              value={leadForm.priority}
              onChange={handleLeadChange}
            >
              <option>Low</option>
              <option>Medium</option>
              <option>High</option>
            </select>
          </label>

          <label>
            Follow-Up Date
            <input
              type="date"
              name="followUpDate"
              value={leadForm.followUpDate}
              onChange={handleLeadChange}
            />
          </label>
        </div>

        <label>
          Notes
          <textarea
            name="notes"
            value={leadForm.notes}
            onChange={handleLeadChange}
          />
        </label>

        <button type="submit" className="procrm-btn-primary" disabled={saving}>
          {saving ? "Saving Lead..." : "Save Lead"}
        </button>
      </form>
    </section>
  );
}

export default AddLead;